import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DEFAULT_TIMEZONE } from "../../config/global";

dayjs.extend(utc);
dayjs.extend(timezone);

// 24:00 is valid ISO-8601 for end-of-day but never appears in this app's
// backend payloads, so we reject it to keep the regex a single responsibility.
const HH_MM_STRICT = /^([01]\d|2[0-3]):([0-5]\d)$/;

// Require a date AND a time component so bare dates ("2026-04-24") don't get
// silently accepted by dayjs() as 04:00 Dubai. Accepts optional seconds and
// any trailing zone info (Z, +04:00, etc.).
const ISO_WITH_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?/;

/** A trailing zone marker ("Z" or "+04:00") — the input carries its own offset. */
const HAS_ZONE = /(Z|[+-]\d{2}:\d{2})$/;

/**
 * Calendar date ("YYYY-MM-DD") of a datetime, resolved in Dubai.
 *
 * Used to tell "this shuttle is for the day my train runs" from "this shuttle is
 * for the day my train USED to run" — after a change-date the old shuttle offer
 * stays on the booking, and nothing else in the entry carries a date.
 *
 * A naive string ("2026-07-28T05:55:00") has no zone: converting it to Dubai
 * would shift the day for anyone browsing west of UTC+4, so only zoned inputs
 * are converted. Returns null when the input is absent or unparseable —
 * callers must treat that as "unknown", never as a mismatch.
 */
export const dubaiDate = (raw?: string | Date | null): string | null => {
  if (!raw) return null;
  const parsed = dayjs(raw);
  if (!parsed.isValid()) return null;
  // A Date is an instant, so it always converts; only a zone-less STRING is
  // taken as Dubai wall-time already written out.
  const zoned = typeof raw !== "string" || HAS_ZONE.test(raw);
  return (zoned ? parsed.tz(DEFAULT_TIMEZONE) : parsed).format("YYYY-MM-DD");
};

/**
 * "HH:mm" of a datetime, resolved in Dubai — the timezone the trains and
 * shuttles actually run in.
 *
 * The offers list and the booked-shuttle normaliser both used `getHours()`,
 * which reads the BROWSER's zone: a shuttle departing 05:00 Dubai showed as
 * 02:00 to a viewer at UTC+1 (observed live 2026-07-27). Same naive-string rule
 * as dubaiDate — an input carrying no zone is displayed as written.
 */
export const hhmmInDubai = (raw?: string | null): string => {
  if (!raw) return "";
  const parsed = dayjs(raw);
  if (!parsed.isValid()) return "";
  const zoned = HAS_ZONE.test(raw);
  return (zoned ? parsed.tz(DEFAULT_TIMEZONE) : parsed).format("HH:mm");
};

/**
 * Resolve a time to minutes-since-midnight in Dubai.
 * Prefers a raw ISO datetime (authoritative); falls back to a strict "HH:MM"
 * display string. Returns null when neither input yields a parseable time —
 * callers should treat null as "no time constraint" and avoid dropping data.
 */
export const minutesSinceMidnightInDubai = (
  isoDateTime?: string | null,
  hhmm?: string | null
): number | null => {
  if (typeof isoDateTime === "string" && ISO_WITH_TIME.test(isoDateTime)) {
    const parsed = dayjs(isoDateTime);
    if (parsed.isValid()) {
      // Same naive-string rule as dubaiDate/hhmmInDubai: a zone-less payload is
      // already Dubai wall-time, so converting it would shift the value. Without
      // this the module answered the same question two ways — a trip LABELLED
      // 05:00 could be bucketed as 08:00 by splitShuttleTrips.
      const local = HAS_ZONE.test(isoDateTime) ? parsed.tz(DEFAULT_TIMEZONE) : parsed;
      return local.hour() * 60 + local.minute();
    }
  }
  if (typeof hhmm === "string" && HH_MM_STRICT.test(hhmm)) {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  }
  return null;
};
